import { HttpErrorResponse } from '@angular/common/http';
import { FirebaseError } from 'firebase/app';
import { CloudError } from './cloud-error';
import { StackFrame } from 'stacktrace-js';
import { ZodError } from 'zod';

export function isFirebaseNetworkError(error: unknown): error is FirebaseError {
  return error instanceof FirebaseError && error.code === 'auth/network-request-failed';
}

export function isFirebaseCustomTokenError(error: unknown): error is FirebaseError {
  return error instanceof FirebaseError && error.code === 'auth/invalid-custom-token';
}

interface FirebaseErrorDetails {
  code: string;
  message: string;
  customData?: Record<string, unknown>;
  underlyingMessage?: string;
}

export function extractFirebaseErrorDetails(error: FirebaseError): string {
  const details: FirebaseErrorDetails = {
    code: error.code,
    message: error.message,
  };

  // Extract customData if present (contains additional error info)
  if (error.customData && typeof error.customData === 'object') {
    details.customData = error.customData as Record<string, unknown>;
  }

  // Try to extract underlying message from the error message
  // Firebase often wraps errors like: "Firebase: Error (auth/network-request-failed)."
  // or "Firebase: <underlying message> (auth/code)."
  const messageMatch = error.message.match(/Firebase:\s*(.+?)\s*\(auth\//);
  if (messageMatch && messageMatch[1] && messageMatch[1] !== 'Error') {
    details.underlyingMessage = messageMatch[1].trim();
  }

  return formatFirebaseErrorDetails(details);
}

interface InnerError {
  code: string;
  message: string;
}

export function normalizeError(err: unknown): Error {
  if (err instanceof HttpErrorResponse) {
    const innerError = err?.error?.errors;
    if (isInnerErrorArray(innerError) && innerError.length > 0) {
      const newError = new CloudError(innerError[0].message, innerError[0].code);
      newError.stack = `HttpErrorResponse error: [${err.status}] ${err.message}\n url: ${err.url}}\n innerError: ${innerError[0].message}`;
      return newError;
    }
    const newError = new Error(err.message);
    newError.stack = `HttpErrorResponse error: [${err.status}] ${err.message}\n url: ${err.url}}`;
    return newError;
  }

  if (err instanceof ZodError) {
    const zodIssues = err.issues
      .map((issue) => {
        const path = issue.path.length > 0 ? issue.path.join('.') : 'root';
        return `Path: ${path}, Message: ${issue.message}`;
      })
      .join('\n');
    return new Error(zodIssues);
  }

  if (typeof DOMException !== 'undefined' && err instanceof DOMException) {
    return new Error(`DOMException: ${err.name} - ${err.message}`);
  }

  if (err instanceof Error) {
    return err;
  }

  if (typeof err === 'object' && err !== null) {
    try {
      return new Error(JSON.stringify(err, null, 2));
    } catch {
      return new Error('[Unknown object with circular structure]');
    }
  }

  if (typeof err === 'string') {
    return new Error(err);
  }

  if (typeof err === 'number' || typeof err === 'boolean') {
    return new Error(`Non-error thrown: ${String(err)}`);
  }

  // Fallback
  return new Error('Unknown error');
}

export async function captureStackTrace(error: Error): Promise<[string | undefined, StackFrame[]]> {
  try {
    const rawStackFrames = await StackTrace.fromError(error);
    const limitedStack = rawStackFrames.slice(0, 10);
    const stackTrace = limitedStack.map((frame) => frame.toString()).join('\n');
    return [stackTrace, rawStackFrames];
  } catch (stackError) {
    return [undefined, []];
  }
}

function isInnerErrorArray(value: unknown): value is InnerError[] {
  return (
    Array.isArray(value) &&
    value.every(
      (item) =>
        typeof item === 'object' &&
        'code' in item &&
        typeof item.code === 'string' &&
        'message' in item &&
        typeof item.message === 'string',
    )
  );
}

function formatFirebaseErrorDetails(details: FirebaseErrorDetails): string {
  const parts: string[] = [];

  parts.push(`code=${details.code}`);

  if (details.underlyingMessage) {
    parts.push(`underlying="${details.underlyingMessage}"`);
  }

  if (details.customData && Object.keys(details.customData).length > 0) {
    parts.push(`customData=${JSON.stringify(details.customData)}`);
  }

  return parts.join(', ');
}
